import React, { useRef, useEffect } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useTranslation } from 'react-i18next';
import { GOOGLE_MAP_API_KEY } from '../../constants';
import { LocationAddressType } from '../../models/commonTypeScript';
import InputLabel from '../input/InputLabel';
import styles from './SearchAddress.module.scss';

type Props = {
    address: string;
    onPlaceChanged: (address: LocationAddressType) => void;
    displayValidation: boolean;
    mandatory?: boolean;
    isValid: boolean;
    infoText?: string;
    addressHint?: string;
    infoTextTranslated?: boolean;
};

const SearchAddressInner = (props: Props): React.ReactElement => {
    const {
        onPlaceChanged,
        address,
        displayValidation,
        isValid,
        infoText,
        addressHint = 'GoogleAddressHint',
        infoTextTranslated = false,
        mandatory,
    } = props;

    const { t: txt } = useTranslation();

    const containerRef = useRef<HTMLDivElement>(null);
    const confirmedAddressRef = useRef<string>(address);
    const autocompleteElementRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
    const placesLib = useMapsLibrary('places');

    useEffect(() => {
        if (!placesLib || !containerRef.current) return;

        const placeAutocomplete = new placesLib.PlaceAutocompleteElement({
            types: ['geocode'],
        });

        placeAutocomplete.id = 'searchAddress';
        placeAutocomplete.classList.add(styles.autocompleteElement);
        autocompleteElementRef.current = placeAutocomplete;

        if (address) {
            placeAutocomplete.value = address;
        }

        const handleSelect = async (event: Event): Promise<void> => {
            const { placePrediction } = event as google.maps.places.PlaceAutocompletePlaceSelectEvent;
            if (!placePrediction) return;

            const place = placePrediction.toPlace();
            await place.fetchFields({
                fields: ['formattedAddress', 'addressComponents', 'location'],
            });

            const newAddress = place.formattedAddress ?? '';
            const countryComponent = place.addressComponents?.find(comp => comp.types.includes('country'));
            const postalCodeComponent = place.addressComponents?.find(comp => comp.types.includes('postal_code'));

            if (place.location) {
                placeAutocomplete.value = newAddress;
                confirmedAddressRef.current = newAddress;

                onPlaceChanged({
                    coords: { lat: place.location.lat(), lng: place.location.lng() },
                    address: newAddress,
                    countryCode: countryComponent?.shortText,
                    postalCode: postalCodeComponent?.longText ?? undefined,
                });
            }
        };

        const handleBlur = (): void => {
            placeAutocomplete.value = confirmedAddressRef.current;
        };

        placeAutocomplete.addEventListener('gmp-select', handleSelect);
        placeAutocomplete.addEventListener('blur', handleBlur);

        const container = containerRef.current;
        container.appendChild(placeAutocomplete);

        // eslint-disable-next-line consistent-return
        return () => {
            placeAutocomplete.removeEventListener('gmp-select', handleSelect);
            placeAutocomplete.removeEventListener('blur', handleBlur);
            if (container.contains(placeAutocomplete)) {
                container.removeChild(placeAutocomplete);
            }
        };
    }, [placesLib]);

    useEffect(() => {
        if (!autocompleteElementRef.current) return;
        autocompleteElementRef.current.classList.toggle(styles.invalid, displayValidation && !isValid);
    }, [displayValidation, isValid]);

    return (
        <div className={`input-container ${styles.wrapper}`}>
            <InputLabel
                label="Address"
                mandatory={mandatory}
                infoText={infoText}
                infoTextTranslated={infoTextTranslated}
                htmlFor="searchAddress"
            />
            <div ref={containerRef} />
            {displayValidation && !isValid && addressHint && (
                <div className="input-container__error"> {txt(addressHint)} </div>
            )}
        </div>
    );
};

const SearchAddress = (props: Props): React.ReactElement => (
    <APIProvider apiKey={GOOGLE_MAP_API_KEY} libraries={['places']}>
        <SearchAddressInner {...props} />
    </APIProvider>
);

export default SearchAddress;
